/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Heading from '@atlaskit/heading';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { type OptionType } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { BulkIssueSelector, IssueSelector } from '../../common/ui/issue-selector';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';
import { TransitionSelector } from './transition-selector';

const styles = cssMap({
	negativeMarginTop: {
		// This is rendered inside a Box with default top padding within `StoreAssistantView`
		// This action does not have render a response from the assistant, so we need to negate this extra padding
		marginTop: token('space.negative.150'),
	},
});

const JiraIssueTransitionActionNewLayout = ({
	actions,
	getReadonly,
	onCustomDataConfirm,
	onActioned,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: ActionRendererFor<typeof ActionKeys.JiraIssueTransition>): JSX.Element => {
	di(IssueSelector);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [selectedTransition, setSelectedTransition] = useState<OptionType | undefined>(undefined);
	const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	//Use transition id of first action as default transition id
	const defaultTransitionId = actions[0].data.status;
	const allActionsAreReadonly = actions.every((action) => getReadonly(action));
	const initialIssues = actions.map((action) => {
		return {
			issueKey: action.data.key,
			readonly: getReadonly(action),
			url: action.data.url,
		};
	});
	const handleSubmit = useCallback(async () => {
		if (!selectedIssues.length || allActionsAreReadonly) {
			return;
		}
		let actionsToSubmit: ActionWith<
			ActionRendererKeyToPayload[typeof ActionKeys.JiraIssueTransition]
		>[] = [];
		setIsLoading(true);
		try {
			const actionsMap = new Map(actions.map((action) => [action.data.key, action]));
			actionsToSubmit = selectedIssues
				.map((issueKey) => actionsMap.get(issueKey))
				.filter((action) => action !== undefined)
				.filter((action) => !getReadonly(action));

			if (
				FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
			) {
				const plannedActions = actions.filter((action) => action.status === 'planned');
				const actionsToSubmitKeys = new Set(actionsToSubmit.map((action) => action.data.key));

				sendMessageConfirmedTools({
					agentId: recipientAgentId,
					conversationId,
					messageSources: {
						interactionSource: 'jiraIssueTransitionRenderer',
					},
					confirmed_tools: {
						message_id: messageId,
						tools: plannedActions.map((action) =>
							actionsToSubmitKeys.has(action.data.key)
								? buildToolFromAction(action, 'confirmed', {
										key: action.data.key,
										status: selectedTransition?.value,
										url: action.data.url,
										ari: action.data.ari,
									})
								: buildToolFromAction(action, 'cancelled'),
						),
					},
				});
				sendAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'rovoJiraIssueTransition',
					attributes: {
						issueCount: actionsToSubmit.length,
					},
				});
				return;
			}

			const submissionPromises = actionsToSubmit.map(async (action) => {
				await onCustomDataConfirm(action, {
					key: action.data.key,
					status: selectedTransition?.value,
					url: action.data.url,
					ari: action.data.ari,
				});
			});

			await Promise.all(submissionPromises);
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'rovoJiraIssueTransition',
				attributes: {
					issueCount: actionsToSubmit.length,
				},
			});
		} catch (e) {
			// TODO: remove catch block once resumption is cleaned up
			if (e instanceof Error) {
				actionsToSubmit.forEach((action) => {
					onActioned(action, e.message);
				});
			} else {
				actionsToSubmit.forEach((action) => {
					onActioned(action, formatMessage(messages.jiraBulkIssueTransitionErrorMessage));
				});
			}
		} finally {
			setIsLoading(false);
		}
	}, [
		selectedIssues,
		allActionsAreReadonly,
		actions,
		sendAnalyticsEvent,
		getReadonly,
		onCustomDataConfirm,
		selectedTransition?.value,
		onActioned,
		formatMessage,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
	]);

	const handleCancel = useCallback(() => {
		if (
			!FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			return;
		}
		const plannedActions = actions.filter((action) => action.status === 'planned');
		if (!plannedActions.length) {
			return;
		}
		sendMessageConfirmedTools({
			agentId: recipientAgentId,
			conversationId,
			messageSources: {
				interactionSource: 'jiraIssueTransitionRenderer',
			},
			confirmed_tools: {
				message_id: messageId,
				tools: plannedActions.map((action) => buildToolFromAction(action, 'cancelled')),
			},
		});
	}, [actions, sendMessageConfirmedTools, recipientAgentId, conversationId, messageId]);

	const handleSelectIssue = useCallback((issueKeys: string[]) => {
		setSelectedIssues(issueKeys);
	}, []);
	return (
		<Stack space="space.250" xcss={styles.negativeMarginTop}>
			<Text>{formatMessage(messages.jiraIssueTransitionTitle)}</Text>

			<ActionCardWrapper
				title={formatMessage(messages.jiraIssueTransitionActionCardTitle)}
				product="jira"
			>
				<Stack space="space.150">
					<Inline alignBlock="center" space="space.150">
						<Heading size="xsmall">
							{formatMessage(messages.jiraIssueTransitionStatusTitle)}
						</Heading>
						<TransitionSelector
							issueAris={actions.map((action) => action.data.ari)}
							setSelectedTransition={setSelectedTransition}
							defaultTransitionId={defaultTransitionId}
							selectedTransition={selectedTransition}
							isDisabled={allActionsAreReadonly}
						/>
					</Inline>
					<BulkIssueSelector
						initialIssues={initialIssues}
						title={formatMessage(messages.jiraIssueTransitionSelectIssuesTitle)}
						onIssuesSelected={handleSelectIssue}
						readonly={allActionsAreReadonly}
					/>
				</Stack>

				<ActionCardFooter>
					<Inline space="space.100" alignInline="end">
						{FeatureGates.getExperimentValue(
							'rovo_tools_resumption_migration_exp',
							'isEnabled',
							false,
						) && (
							<Button
								appearance="subtle"
								isDisabled={allActionsAreReadonly}
								onClick={(_, e) => {
									handleCancel();
									sendAnalyticsEvent(
										e.update({
											actionSubjectId: 'rovoJiraIssueTransitionCancel',
										}),
									);
								}}
							>
								{formatMessage(messages.jiraIssueTransitionCancel)}
							</Button>
						)}
						<Button
							appearance="primary"
							type="submit"
							isDisabled={allActionsAreReadonly || !selectedIssues.length}
							isLoading={isLoading}
							onClick={(_, e) => {
								handleSubmit();
								sendAnalyticsEvent(
									e.update({
										actionSubjectId: 'rovoJiraIssueTransitionSubmit',
									}),
								);
							}}
							interactionName="ai-mate.issue-bulk-transition"
						>
							{formatMessage(messages.jiraIssueTransitionUpdateSelectedIssues)}
						</Button>
					</Inline>
				</ActionCardFooter>
			</ActionCardWrapper>
		</Stack>
	);
};

export const JiraIssueTransitionAction = (
	props: ActionRendererFor<typeof ActionKeys.JiraIssueTransition>,
): JSX.Element => {
	return <JiraIssueTransitionActionNewLayout {...props} />;
};
